// Frontend filtering for cards query blocks.
// Supports two modes via filter wrapper data attribute:
// - member mode: select (region) + A-Z buttons
// - taxonomy mode: taxonomy term buttons ("Alle" + terms)
//
// Expected wrappers:
// - [data-cards-block]
// - .cards__filters
//
// Expected card data attributes:
// - member mode: data-card-group + data-card-alpha
// - taxonomy mode: data-card-filter

const SELECTORS = {
  blockRoot: "[data-cards-block]",
  filterWrap: ".cards__filters",
  filterSelect: ".cards__filter-select",
  alphaButton: ".cards__filter-alpha-btn",
  taxonomyButton: ".cards__filter-taxonomy-btn",
  emptyState: ".cards__filter-empty",
  card: ".card",
  countRow: ".cards__count",
  countText: ".cards__filter-count",
};

const FILTER_MODE = {
  MEMBER: "member",
  TAXONOMY: "taxonomy",
};

function escapeSelectorValue(value) {
  const raw = String(value || "");

  if (
    typeof window !== "undefined" &&
    window.CSS &&
    typeof window.CSS.escape === "function"
  ) {
    return window.CSS.escape(raw);
  }

  return raw.replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, "\\$1");
}

function formatCountText(visibleCount, totalCount, suffix = "Ergebnissen") {
  return `${visibleCount} von ${totalCount} ${suffix}`;
}

function setButtonState(buttons, activeValue, key = "filter") {
  buttons.forEach((button) => {
    const currentValue = button.dataset[key] || "";
    const isActive = currentValue === activeValue;
    button.classList.toggle("is-active", isActive);
    button.setAttribute("aria-pressed", isActive ? "true" : "false");
  });
}

function ensureCountNode(blockRoot, filterWrap, totalCount, countLabel) {
  let row = blockRoot.querySelector(SELECTORS.countRow);
  let textNode = row ? row.querySelector(SELECTORS.countText) : null;

  if (!row) {
    row = document.createElement("div");
    row.className = "cards__count";

    if (filterWrap && filterWrap.parentNode) {
      filterWrap.parentNode.insertBefore(row, filterWrap.nextSibling);
    } else {
      blockRoot.appendChild(row);
    }
  }

  if (!textNode) {
    textNode = document.createElement("p");
    textNode.className = "cards__filter-count js-cards-filter-count";
    textNode.setAttribute("aria-live", "polite");
    row.appendChild(textNode);
  }

  textNode.textContent = formatCountText(totalCount, totalCount, countLabel);
  return textNode;
}

function setCardVisibility(card, isVisible) {
  card.hidden = !isVisible;
  card.classList.toggle("is-hidden", !isVisible);

  if (isVisible) {
    card.style.removeProperty("display");
    card.removeAttribute("aria-hidden");
  } else {
    card.style.setProperty("display", "none", "important");
    card.setAttribute("aria-hidden", "true");
  }
}

function applyMemberFilters({
  cards,
  activeGroup,
  activeAlpha,
  emptyState,
  countNode,
  totalCount,
  countLabel,
}) {
  let visibleCount = 0;

  cards.forEach((card) => {
    const cardGroup = card.getAttribute("data-card-group") || "";
    const cardAlpha = card.getAttribute("data-card-alpha") || "";

    const groupMatch = !activeGroup || cardGroup === activeGroup;
    const alphaMatch = !activeAlpha || cardAlpha === activeAlpha;
    const isVisible = groupMatch && alphaMatch;

    setCardVisibility(card, isVisible);

    if (isVisible) {
      visibleCount += 1;
    }
  });

  if (emptyState) {
    emptyState.hidden = visibleCount > 0;
  }

  if (countNode) {
    countNode.textContent = formatCountText(
      visibleCount,
      totalCount,
      countLabel,
    );
  }
}

function applyTaxonomyFilters({
  cards,
  activeFilter,
  emptyState,
  countNode,
  totalCount,
  countLabel,
}) {
  let visibleCount = 0;

  cards.forEach((card) => {
    const cardFilter = card.getAttribute("data-card-filter") || "";
    const isVisible = !activeFilter || cardFilter === activeFilter;

    setCardVisibility(card, isVisible);

    if (isVisible) {
      visibleCount += 1;
    }
  });

  if (emptyState) {
    emptyState.hidden = visibleCount > 0;
  }

  if (countNode) {
    countNode.textContent = formatCountText(
      visibleCount,
      totalCount,
      countLabel,
    );
  }
}

function inferMode(filterWrap, hasSelect, alphaButtons, taxonomyButtons) {
  const explicitMode = (
    filterWrap.getAttribute("data-filter-mode") || ""
  ).trim();
  if (
    explicitMode === FILTER_MODE.MEMBER ||
    explicitMode === FILTER_MODE.TAXONOMY
  ) {
    return explicitMode;
  }

  if (hasSelect || alphaButtons.length > 0) return FILTER_MODE.MEMBER;
  if (taxonomyButtons.length > 0) return FILTER_MODE.TAXONOMY;
  return "";
}

function initBlockFilter(blockRoot) {
  const filterWrap = blockRoot.querySelector(SELECTORS.filterWrap);
  if (!filterWrap) return;

  const gridId = filterWrap.getAttribute("data-filter-target");
  const cardsGrid = gridId
    ? blockRoot.querySelector(`#${escapeSelectorValue(gridId)}`)
    : null;

  if (!cardsGrid) return;

  const cards = Array.from(cardsGrid.querySelectorAll(SELECTORS.card));
  if (!cards.length) return;

  const select = filterWrap.querySelector(SELECTORS.filterSelect);
  const alphaButtons = Array.from(
    filterWrap.querySelectorAll(SELECTORS.alphaButton),
  );
  const taxonomyButtons = Array.from(
    filterWrap.querySelectorAll(SELECTORS.taxonomyButton),
  );

  const mode = inferMode(filterWrap, !!select, alphaButtons, taxonomyButtons);
  if (!mode) return;

  const emptyState = blockRoot.querySelector(SELECTORS.emptyState);
  const totalCount = cards.length;
  const countLabel = (
    filterWrap.getAttribute("data-count-label") || "Ergebnissen"
  ).trim();
  const countNode = ensureCountNode(
    blockRoot,
    filterWrap,
    totalCount,
    countLabel,
  );

  if (mode === FILTER_MODE.MEMBER) {
    let activeGroup = select ? select.value || "" : "";
    let activeAlpha =
      alphaButtons.find((btn) => btn.classList.contains("is-active"))?.dataset
        .alpha || "";

    setButtonState(alphaButtons, activeAlpha, "alpha");

    const runMember = () =>
      applyMemberFilters({
        cards,
        activeGroup,
        activeAlpha,
        emptyState,
        countNode,
        totalCount,
        countLabel,
      });

    if (select) {
      select.addEventListener("change", () => {
        activeGroup = select.value || "";
        runMember();
      });
    }

    alphaButtons.forEach((button) => {
      button.addEventListener("click", () => {
        activeAlpha = button.dataset.alpha || "";
        setButtonState(alphaButtons, activeAlpha, "alpha");
        runMember();
      });
    });

    runMember();
    return;
  }

  if (mode === FILTER_MODE.TAXONOMY) {
    let activeFilter =
      taxonomyButtons.find((btn) => btn.classList.contains("is-active"))
        ?.dataset.filter || "";

    setButtonState(taxonomyButtons, activeFilter, "filter");

    const runTaxonomy = () =>
      applyTaxonomyFilters({
        cards,
        activeFilter,
        emptyState,
        countNode,
        totalCount,
        countLabel,
      });

    taxonomyButtons.forEach((button) => {
      button.addEventListener("click", () => {
        activeFilter = button.dataset.filter || "";
        setButtonState(taxonomyButtons, activeFilter, "filter");
        runTaxonomy();
      });
    });

    runTaxonomy();
  }
}

export default function initCardsFilters(root = document) {
  const blockRoots = Array.from(root.querySelectorAll(SELECTORS.blockRoot));
  if (!blockRoots.length) return;

  blockRoots.forEach(initBlockFilter);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initCardsFilters());
} else {
  initCardsFilters();
}
